<template>
  <div id="app">
    <!-- Skip links for accessibility -->
    <SkipLinks />
    
    <NavbarMenu 
      id="navigation"
      @invite-bot="inviteBot" 
      @join-server="joinServer" 
      @become-patron="becomePatron" 
    />
    
    <main id="main-content" tabindex="-1" role="main">
      <router-view />
    </main>
    
    <FooterMenu />
  </div>
</template>

<script>
import NavbarMenu from './components/NavbarMenu.vue'
import FooterMenu from './components/FooterMenu.vue'
import SkipLinks from './components/SkipLinks.vue'

export default {
  name: 'App',
  components: {
    NavbarMenu,
    FooterMenu,
    SkipLinks
  },
  data() {
    return {
      // App state data can be added here as needed
    }
  },
  mounted() {
    // Performance optimizations
    this.setupPerformanceOptimizations();
    
    // Accessibility improvements
    this.setupAccessibilityFeatures();
    
    // Handle font loading
    this.handleFontLoading();
  },
  methods: {
    setupPerformanceOptimizations() {
      // Prefetch critical resources
      this.prefetchResources();
      
      // Setup performance monitoring
      this.setupPerformanceMonitoring();
    },
    
    handleFontLoading() {
      // Modern gaming fonts are loaded via Google Fonts
      // No need for additional font loading logic since we're using web fonts with display: swap
      console.log('Using modern Exo 2 font from Google Fonts');
    },
    
    setupAccessibilityFeatures() {
      // Create live region for dynamic announcements
      const liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('class', 'sr-only');
      liveRegion.id = 'live-region';
      document.body.appendChild(liveRegion);
      
      // Add keyboard navigation helpers
      this.setupKeyboardNavigation();
    },
    
    setupKeyboardNavigation() {
      document.addEventListener('keydown', (event) => {
        // ESC key to close modals/menus
        if (event.key === 'Escape') {
          // Emit escape event that components can listen to
          this.$root.$emit('escape-pressed');
        }
        
        // Skip links with Alt + S
        if (event.altKey && event.key.toLowerCase() === 's') {
          event.preventDefault();
          const skipLinks = document.querySelector('.skip-link');
          if (skipLinks) {
            skipLinks.focus();
          }
        }
      });
    },
    
    announceToScreenReader(message) {
      const liveRegion = document.getElementById('live-region');
      if (liveRegion) {
        liveRegion.textContent = message;
        setTimeout(() => {
          liveRegion.textContent = '';
        }, 1000);
      }
    },
    
    prefetchResources() {
      // Prefetch important images and resources
      const criticalImages = [
        '/src/assets/logo.png',
        '/src/assets/control_panel.png',
        '/src/assets/user_management2.png',
        '/src/assets/channel_management.png'
      ];
      
      criticalImages.forEach(src => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = src;
        document.head.appendChild(link);
      });
    },
    
    setupPerformanceMonitoring() {
      // Track Core Web Vitals
      if ('performance' in window && 'PerformanceObserver' in window) {
        try {
          const observer = new PerformanceObserver((list) => {
            list.getEntries().forEach((entry) => {
              // Log performance metrics for monitoring
              if (process.env.NODE_ENV === 'development') {
                console.log(`${entry.name}: ${entry.value}ms`);
              }
            });
          });
          
          observer.observe({ entryTypes: ['measure', 'navigation'] });
        } catch (error) {
          console.warn('Performance monitoring not available:', error);
        }
      }
    },
    
    inviteBot() {
      // Add loading state for better UX
      this.announceToScreenReader('Opening Discord bot invitation in new tab');
      
      window.open(
        'https://discord.com/oauth2/authorize?client_id=756856376201969716&permissions=947326938200&scope=bot%20applications.commands',
        '_blank',
        'noopener,noreferrer'
      );
    },
    
    joinServer() {
      this.announceToScreenReader('Opening Discord support server in new tab');
      
      window.open(
        'https://discord.com/invite/beaStFd',
        '_blank',
        'noopener,noreferrer'
      );
    },
    
    becomePatron() {
      this.announceToScreenReader('Opening Patreon page in new tab');
      
      window.open(
        'https://www.patreon.com/partycord',
        '_blank',
        'noopener,noreferrer'
      );
    }
  }
}
</script>

<style>
/* Import global styles */
@import './assets/styles.css';

/* App-specific styles */
#app {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-loading {
  overflow: hidden;
}

/* Ensure router-view takes available space */
#app > .router-view,
#app > main {
  flex: 1;
  position: relative;
  z-index: 1;
}

/* Smooth transitions between routes */
.page-enter-active,
.page-leave-active {
  transition: all 0.3s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Print styles */
@media print {
  .navbar,
  .footer,
  .discord-button,
  .patreon-button {
    display: none !important;
  }
  
  .content {
    padding: 0 !important;
    max-width: none !important;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  :root {
    --color-bg-primary: #000000;
    --color-bg-secondary: #111111;
    --color-text-primary: #ffffff;
    --color-text-secondary: #cccccc;
  }
}

/* Performance optimizations */
img {
  content-visibility: auto;
}

/* Improve font rendering */
* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Animations - avoid will-change: transform as it creates stacking context issues */
.fade-in-up,
.supporter-card,
.feature,
.staff-card {
  will-change: opacity;
}

/* Remove will-change after animations complete */
.fade-in-up.animate-in,
.supporter-card.animate-in,
.feature.animate-in,
.staff-card.animate-in {
  will-change: auto;
}
</style>